import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { OverallPerformance } from '@/components/reports/OverallPerformance';
import { TechniciansReport } from '@/components/reports/TechniciansReport';
import { SingleTechReport } from '@/components/reports/SingleTechReport';
import { CsrReport } from '@/components/reports/CsrReport';
import { PostcodeReport } from '@/components/reports/PostcodeReport';
import { LeadSourceCampaignTabs } from '@/components/reports/LeadSourceCampaignTabs';
import { MetroReport } from '@/components/reports/MetroReport';
import { ExpensesReport } from '@/components/reports/ExpensesReport';
import { PartsReport } from '@/components/reports/PartsReport';
import { FinanceReport } from '@/components/reports/FinanceReport';
import { ReviewsReport } from '@/components/reports/ReviewsReport';
import { ReferralsReport } from '@/components/reports/ReferralsReport';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useVisibility } from '@/hooks/useVisibility';
import { getComparisonPeriod } from '@/utils/periodComparison';

/** Underline tab style: 2px primary underline on active, 13px labels */
const underlineTabClass =
  'rounded-none border-b-2 border-transparent bg-transparent px-3 pb-2.5 pt-1.5 text-[13px] font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';

const Reports = () => {
  const { timezone, timezoneShortCode, startOfDay, endOfDay, formatDate } = useTimezoneUtils();
  // Non-admins get the Technicians tab (one tech at a time) and their own CSR row only.
  const { canSeeCompanyFinancials: isAdmin } = useVisibility();

  const [startDate, setStartDate] = useState<Date>(() => startOfDay(new Date()));
  const [endDate, setEndDate] = useState<Date>(() => endOfDay(new Date()));
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [reportGenerated] = useState<Date>(new Date());

  const handleDateChange = (from: Date | undefined, to: Date | undefined) => {
    // The "All Time" preset hands back undefined on both ends: widen the range instead of ignoring it.
    setStartDate(from ?? new Date(2000, 0, 1));
    setEndDate(to ?? endOfDay(new Date()));
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              Generated {formatDate(reportGenerated, 'PPpp')} {timezoneShortCode}
            </p>
          </div>

          {/* Date Range Picker */}
          <DateRangePicker
            dateFrom={startDate}
            dateTo={endDate}
            onDateChange={handleDateChange}
            compareEnabled={compareEnabled}
            onCompareChange={setCompareEnabled}
          />
        </div>

        {/* Current date range indicator with comparison period */}
        {(() => {
          const comparisonPeriod = getComparisonPeriod(startDate, endDate);
          return (
            <div className="inline-flex flex-wrap items-center gap-x-1 rounded-md border bg-card px-2.5 py-1 text-xs text-muted-foreground">
              <span>Showing</span>
              <span className="font-medium text-foreground">{formatDate(startDate, 'PPP')}</span>
              <span>to</span>
              <span className="font-medium text-foreground">{formatDate(endDate, 'PPP')}</span>
              <span>({timezoneShortCode})</span>
              <span className="ml-1">
                · Comparing to {formatDate(comparisonPeriod.start, 'PP')} – {formatDate(comparisonPeriod.end, 'PP')}
              </span>
            </div>
          );
        })()}

        {/* Tabs — underline style */}
        <Tabs defaultValue={isAdmin ? 'overall' : 'technicians'} className="space-y-6">
          <TabsList className="h-auto w-full flex-wrap justify-start gap-1 rounded-none border-b bg-transparent p-0">
            {isAdmin && <TabsTrigger value="overall" className={underlineTabClass}>Overall performance</TabsTrigger>}
            <TabsTrigger value="technicians" className={underlineTabClass}>Technicians</TabsTrigger>
            <TabsTrigger value="csrs" className={underlineTabClass}>CSRs</TabsTrigger>
            {isAdmin && <TabsTrigger value="lead-sources" className={underlineTabClass}>Advertising</TabsTrigger>}
            {isAdmin && <TabsTrigger value="metros" className={underlineTabClass}>Metros</TabsTrigger>}
            {isAdmin && <TabsTrigger value="postcodes" className={underlineTabClass}>Postcodes</TabsTrigger>}
            {isAdmin && <TabsTrigger value="finance" className={underlineTabClass}>Finance</TabsTrigger>}
            {isAdmin && <TabsTrigger value="parts" className={underlineTabClass}>Parts</TabsTrigger>}
            {isAdmin && <TabsTrigger value="reviews" className={underlineTabClass}>Reviews</TabsTrigger>}
            {isAdmin && <TabsTrigger value="referrals" className={underlineTabClass}>Referrals</TabsTrigger>}
          </TabsList>

          {isAdmin && (
            <TabsContent value="overall" className="space-y-4">
              <Card>
                <CardContent className="pt-6">
                  <OverallPerformance startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="technicians">
            {isAdmin ? (
              <TechniciansReport startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
            ) : (
              <SingleTechReport startDate={startDate} endDate={endDate} timezone={timezone} />
            )}
          </TabsContent>

          <TabsContent value="csrs">
            <CsrReport startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="lead-sources">
              <LeadSourceCampaignTabs startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
            </TabsContent>
          )}

            {isAdmin && (
              <TabsContent value="metros">
                <MetroReport startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
              </TabsContent>
            )}

            {isAdmin && (
              <TabsContent value="postcodes">
                <PostcodeReport startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
              </TabsContent>
            )}

            {isAdmin && (
              <TabsContent value="finance">
                <FinanceReport startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
              </TabsContent>
            )}

            {isAdmin && (
              <TabsContent value="parts">
                <PartsReport startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
              </TabsContent>
            )}

            {isAdmin && (
              <TabsContent value="reviews">
                <ReviewsReport startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
              </TabsContent>
            )}

            {isAdmin && (
              <TabsContent value="referrals">
                <ReferralsReport startDate={startDate} endDate={endDate} timezone={timezone} compareEnabled={compareEnabled} />
              </TabsContent>
            )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default Reports;
